import React, { useState, useRef, useEffect } from 'react';
import { IconSend2, IconPhoto } from '@tabler/icons-react';
import { useMessages } from '../../context/MessageProvider';
import { usePresence } from '../../context/UserPresenceProvider';
import { useUIState } from '../../context/UIStateProvider';
import { useImageUpload } from '../../../../components/common/useImageUpload';
import { db } from '../../../../firebaseConfig';
import { collection, query, orderBy, startAt, endAt, limit, getDocs } from 'firebase/firestore';
import { debounce } from '../../utils/debounce';
import { showErrorToast } from '../../../../utils/toastUtils';
import './ChatWindow.css';


// FIX 7.3: Maximum message length to prevent DoS via oversized messages
const MAX_MESSAGE_LENGTH = 5000;

const MessageInputBar = ({ conversationId }) => {
  const [text, setText] = useState('');

  // Use modular contexts instead of legacy useChat
  const { sendMessage, editMessage, scrollToBottom } = useMessages();
  const { notifyTyping } = usePresence();
  const { replyingTo, setReplyingTo, editingMessage, setEditingMessage } = useUIState();
  const textareaRef = useRef(null);
  const {
    isUploading,
    uploadProgress,
    handleFileSelection,
    uploadFiles,
    reset,
  } = useImageUpload({
    uploadFolder: 'chat_messages',
    allowMultiple: false,
    maxSize: { width: 1600, height: 1600 },
  });

  const [isMentionOpen, setIsMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionResults, setMentionResults] = useState([]);
  const [isMentionSearching, setIsMentionSearching] = useState(false);
  const [mentionedUserIds, setMentionedUserIds] = useState([]);

  // FIX D7: Fix debounce timer leak - use useRef + useEffect cleanup
  // ponytail: native React pattern, prevents memory leaks from uncleared timers
  const debouncedTypingRef = React.useRef(null);
  React.useEffect(() => {
    // Create debounced function
    debouncedTypingRef.current = debounce((isTyping) => {
      if (conversationId && notifyTyping) {
        notifyTyping(conversationId, isTyping);
      }
    }, 2000);
    
    // Cleanup: cancel debounce on unmount or dependency change
    return () => {
      debouncedTypingRef.current?.cancel();
    };
  }, [conversationId, notifyTyping]);
  
  const debouncedTyping = debouncedTypingRef.current;

  // Pre-fill text when editing
  useEffect(() => {
    if (editingMessage) {
      setText(editingMessage.content || '');
      textareaRef.current?.focus();
    }
  }, [editingMessage]);

  const handleTextChange = (e) => {
    const value = e.target.value;
    setText(value);

    // Debounced typing notification
    debouncedTyping(value.trim().length > 0);

    // Auto-expand textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = '42px';
      const scrollHeight = textareaRef.current.scrollHeight;
      textareaRef.current.style.height = `${Math.min(scrollHeight, 120)}px`;
    }

    const target = e.target;
    const caret = target.selectionStart || value.length;
    const upToCaret = value.slice(0, caret);
    const match = /(^|\s)@([^\s@]{1,32})$/i.exec(upToCaret);
    if (match) {
      const q = match[2].toLowerCase();
      setMentionQuery(q);
      setIsMentionOpen(true);
    } else {
      setMentionQuery('');
      setIsMentionOpen(false);
      setMentionResults([]);
    }
  };

  // Reset height when text is cleared
  useEffect(() => {
    if (!text && textareaRef.current) {
      textareaRef.current.style.height = '42px';
    }
  }, [text]);

  useEffect(() => {
    let active = true;
    if (!isMentionOpen || !mentionQuery) {
      setMentionResults([]);
      setIsMentionSearching(false);
      return undefined;
    }

    setIsMentionSearching(true);
    const timer = setTimeout(async () => {
      try {
        const usersRef = collection(db, 'users');
        const q = query(
          usersRef,
          orderBy('username_lower'),
          startAt(mentionQuery),
          endAt(mentionQuery + '\uf8ff'),
          limit(5),
        );
        const snapshot = await getDocs(q);
        if (!active) return;
        const items = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
        setMentionResults(items);
      } catch {
        if (!active) return;
        setMentionResults([]);
      } finally {
        if (active) {
          setIsMentionSearching(false);
        }
      }
    }, 200);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [isMentionOpen, mentionQuery]);

  const handleSelectMention = (user) => {
    if (!user?.id) return;
    const label = user.displayName || user.username || user.name || 'user';
    setText((prev) => {
      const textarea = textareaRef.current;
      const caret = textarea && typeof textarea.selectionStart === 'number' ? textarea.selectionStart : prev.length;
      const upToCaret = prev.slice(0, caret);
      const afterCaret = prev.slice(caret);
      const match = /(^|\s)@([^\s@]{0,32})$/i.exec(upToCaret);
      if (!match) return prev;
      const startIndex = match.index;
      const prefix = upToCaret.slice(0, startIndex);
      const leading = match[1] || '';
      const insertion = `${leading}@${label} `;
      return `${prefix}${insertion}${afterCaret}`;
    });
    setMentionedUserIds((prev) => (prev.includes(user.id) ? prev : [...prev, user.id]));
    setIsMentionOpen(false);
    setMentionQuery('');
    setMentionResults([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const value = text.trim();
    if (!value || !conversationId) return;

    // FIX 7.3: Reject oversized messages before processing
    if (value.length > MAX_MESSAGE_LENGTH) {
      showErrorToast(`Message too long (${value.length}/${MAX_MESSAGE_LENGTH} characters)`);
      return;
    }

    try {
      if (editingMessage) {
        // Edit mode - OPTIMISTIC / NON-BLOCKING (matches send pattern)
        // 1. Clear UI immediately (0ms latency feedback)
        const messageId = editingMessage.id;
        const originalContent = editingMessage.content;

        setText('');
        setEditingMessage(null);

        // 2. Fire and forget (ChatContext handles optimistic update + failure state)
        editMessage(messageId, value, originalContent).catch(error => {
          console.error('[Chat] Failed to edit message', error);
          // Note: The message will revert to original content on failure via ChatContext
        });
      } else {
        // Send mode - OPTIMISTIC / NON-BLOCKING
        // 1. Clear UI immediately (0ms latency feedback)
        const currentReplyTo = replyingTo;
        const currentMentions = mentionedUserIds;

        setText('');
        setMentionedUserIds([]);
        setReplyingTo(null);

        // 2. Fire and forget (ChatContext handles optimistic update + failure state)
        sendMessage({
          conversationId,
          content: value,
          mentionedUserIds: currentMentions,
          replyTo: currentReplyTo
        }).catch(error => {
          console.error('[Chat] Failed to send message', error);
          // Note: The message will show as "Failed" in the list via ChatContext state
        });
        
        // Ensure the user sees the message they just sent
        setTimeout(() => scrollToBottom(), 50);
      }

      if (conversationId && notifyTyping) {
        notifyTyping(conversationId, false);
      }
    } catch (error) {
      console.error('[Chat] Failed to send/edit message', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingMessage(null);
    setText('');
  };

  const handleKeyDown = (e) => {
    // Enter key to send, Shift+Enter for new line
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };



  return (
    <>
      {/* Editing Preview */}
      {editingMessage && (
        <div className="chat-input-bar__reply-preview" style={{ borderLeftColor: '#f59e0b' }}>
          <div className="chat-input-bar__reply-bar" />
          <div className="chat-input-bar__reply-content">
            <div className="chat-input-bar__reply-sender" style={{ color: '#f59e0b' }}>
              Editing message
            </div>
            <div className="chat-input-bar__reply-text">
              {editingMessage.content}
            </div>
          </div>
          <button
            type="button"
            className="chat-input-bar__reply-close"
            onClick={handleCancelEdit}
            aria-label="Cancel edit"
          >
            ×
          </button>
        </div>
      )}

      {/* Reply Preview */}
      {replyingTo && !editingMessage && (
        <div className="chat-input-bar__reply-preview">
          <div className="chat-input-bar__reply-bar" />
          <div className="chat-input-bar__reply-content">
            <div className="chat-input-bar__reply-sender">
              Replying to {replyingTo.senderName || 'Someone'}
            </div>
            <div className="chat-input-bar__reply-text">
              {replyingTo.content}
            </div>
          </div>
          <button
            type="button"
            className="chat-input-bar__reply-close"
            onClick={() => setReplyingTo(null)}
            aria-label="Cancel reply"
          >
            ×
          </button>
        </div>
      )}

      <form className="chat-input-bar" onSubmit={handleSubmit} aria-label="Send message">
        <div className="chat-input-bar__input-wrapper">
          <textarea
            className="chat-input-bar__textarea"
            placeholder="Type a message"
            value={text}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            ref={textareaRef}
            rows={1}
          />
          {isMentionOpen && mentionResults.length > 0 && (
            <div className="chat-input-bar__mentions">
              {isMentionSearching && (
                <div className="chat-input-bar__mentions-status">Searching...</div>
              )}
              {mentionResults.map((user) => (
                <button
                  key={user.id}
                  type="button"
                  className="chat-input-bar__mentions-item"
                  onClick={() => handleSelectMention(user)}
                >
                  <span className="chat-input-bar__mentions-name">
                    {user.displayName || user.username || user.name || 'Unnamed user'}
                  </span>
                  {user.userProfession && (
                    <span className="chat-input-bar__mentions-meta">{user.userProfession}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className={`chat-input-bar__right-group ${text.trim() ? 'chat-input-bar__right-group--stacked' : ''}`}>
          <label className="chat-input-bar__attach" aria-label="Attach image">
            <input
              type="file"
              accept="image/*"
              multiple={false}
              onChange={async (e) => {
                const files = e.target.files;
                if (!files || !files.length || !conversationId) return;
                const ok = handleFileSelection(files);
                if (!ok) return;
                try {
                  const urls = await uploadFiles(Array.from(files));
                  const imageUrl = Array.isArray(urls) ? urls[0] : urls;
                  if (imageUrl) {
                    // Fire and forget (consistent with text message pattern)
                    sendMessage({
                      conversationId,
                      content: text.trim() || '',
                      imageUrl,
                      mentionedUserIds,
                    }).catch(error => {
                      console.error('[Chat] Failed to send image message', error);
                    });
                    
                    // Ensure the user sees the image they just sent
                    setTimeout(() => scrollToBottom(), 50);
                    
                    setText('');
                    setMentionedUserIds([]);
                    if (conversationId && notifyTyping) {
                      notifyTyping(conversationId, false);
                    }
                  }
                } catch (error) {
                  console.error('[Chat] Failed to send image message', error);
                } finally {
                  reset(); // Clear internal state
                  if (e.target) e.target.value = ''; // Reset file input
                }
              }}
              style={{ display: 'none' }}
              disabled={isUploading}
            />
            <IconPhoto size={24} />
          </label>

          <button
            type="submit"
            className="chat-input-bar__send"
            disabled={!text.trim() && !isUploading}
            aria-label="Send message"
          >
            <IconSend2 size={24} />
          </button>
        </div>

        {isUploading && (
          <div className="chat-input-bar__upload-status">
            Uploading image... {uploadProgress}%
          </div>
        )}
      </form >
    </>
  );
};

export default MessageInputBar;
